import {
  Order,
  OrderStatus,
  FETCH_ORDERS_REQUEST,
  FETCH_ORDERS_SUCCESS,
  FETCH_ORDERS_FAILURE,
  SEARCH_ORDERS_REQUEST,
  SEARCH_ORDERS_SUCCESS,
  SEARCH_ORDERS_FAILURE,
  FETCH_ORDER_DETAILS_REQUEST,
  FETCH_ORDER_DETAILS_SUCCESS,
  FETCH_ORDER_DETAILS_FAILURE,
  APPROVE_ORDER_REQUEST,
  APPROVE_ORDER_SUCCESS,
  APPROVE_ORDER_FAILURE,
  REJECT_ORDER_REQUEST,
  REJECT_ORDER_SUCCESS,
  REJECT_ORDER_FAILURE,
} from "@/types/models/order";
import { PaginatedApiResponse } from "@/lib/apiClient";
import { AnyAction } from "redux-saga";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface OrderState {
  list: Order[];
  pagination: PaginatedApiResponse<Order[]>["pagination"];
  loading: boolean;
  searchLoading: boolean;
  detailsLoading: boolean;
  loadingById: Record<string, boolean>;
  error: string | null;
  searchResults: Order[];
  searchPagination: PaginatedApiResponse<Order[]>["pagination"];
  selectedOrder: Order | null;
  currentStatus: OrderStatus | null;
}

const initialPagination = {
  total: 0,
  page: 0,
  pageSize: 0,
  totalPages: 0,
};

const initialState: OrderState = {
  list: [],
  pagination: initialPagination,
  loading: false,
  searchLoading: false,
  detailsLoading: false,
  loadingById: {},
  error: null,
  searchResults: [],
  searchPagination: initialPagination,
  selectedOrder: null,
  currentStatus: null,
};

type Action =
  | { type: typeof FETCH_ORDERS_REQUEST }
  | {
      type: typeof FETCH_ORDERS_SUCCESS;
      payload: {
        list: Order[];
        pagination: PaginatedApiResponse<Order[]>["pagination"];
      };
    }
  | { type: typeof FETCH_ORDERS_FAILURE; payload: string }
  | { type: typeof SEARCH_ORDERS_REQUEST }
  | {
      type: typeof SEARCH_ORDERS_SUCCESS;
      payload: {
        list: Order[];
        pagination: PaginatedApiResponse<Order[]>["pagination"];
      };
    }
  | { type: typeof SEARCH_ORDERS_FAILURE; payload: string }
  | { type: typeof FETCH_ORDER_DETAILS_REQUEST; payload: string }
  | { type: typeof FETCH_ORDER_DETAILS_SUCCESS; payload: Order }
  | { type: typeof FETCH_ORDER_DETAILS_FAILURE; payload: string }
  | { type: typeof APPROVE_ORDER_REQUEST; payload: string }
  | { type: typeof APPROVE_ORDER_SUCCESS; payload: string }
  | { type: typeof APPROVE_ORDER_FAILURE; payload: string }
  | { type: typeof REJECT_ORDER_REQUEST; payload: string }
  | { type: typeof REJECT_ORDER_SUCCESS; payload: string }
  | { type: typeof REJECT_ORDER_FAILURE; payload: string }
  | AnyAction;

const orderReducer = (state = initialState, action: Action): OrderState => {
  switch (action.type) {
    case FETCH_ORDERS_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_ORDERS_SUCCESS:
      return {
        ...state,
        loading: false,
        list: action.payload.list,
        pagination: action.payload.pagination,
      };

    case FETCH_ORDERS_FAILURE:
      return { ...state, loading: false, error: action.payload };

    case SEARCH_ORDERS_REQUEST:
      return { ...state, searchLoading: true, error: null };

    case SEARCH_ORDERS_SUCCESS:
      return {
        ...state,
        searchLoading: false,
        searchResults: action.payload.list,
        searchPagination: action.payload.pagination,
      };

    case SEARCH_ORDERS_FAILURE:
      return { ...state, searchLoading: false, error: action.payload };

    case FETCH_ORDER_DETAILS_REQUEST:
      return { ...state, detailsLoading: true, error: null };

    case FETCH_ORDER_DETAILS_SUCCESS:
      return {
        ...state,
        detailsLoading: false,
        selectedOrder: action.payload,
      };

    case FETCH_ORDER_DETAILS_FAILURE:
      return { ...state, detailsLoading: false, error: action.payload };

    case APPROVE_ORDER_REQUEST:
      return {
        ...state,
        loadingById: {
          ...state.loadingById,
          [action.payload]: true,
        },
      };

    case APPROVE_ORDER_SUCCESS:
      return {
        ...state,
        list: state.list.map((order) =>
          order.id === action.payload
            ? { ...order, current_order_stage: "CONFIRMED" as OrderStatus }
            : order,
        ),
        loadingById: {
          ...state.loadingById,
          [action.payload]: false,
        },
      };

    case APPROVE_ORDER_FAILURE:
      return {
        ...state,
        loadingById: {
          ...state.loadingById,
          [action.payload]: false,
        },
        error: action.payload,
      };

    case REJECT_ORDER_REQUEST:
      return {
        ...state,
        loadingById: {
          ...state.loadingById,
          [action.payload]: true,
        },
      };

    case REJECT_ORDER_SUCCESS:
      return {
        ...state,
        list: state.list.map((order) =>
          order.id === action.payload
            ? { ...order, order_status: "CANCELLED" as OrderStatus }
            : order,
        ),
        loadingById: {
          ...state.loadingById,
          [action.payload]: false,
        },
      };

    case REJECT_ORDER_FAILURE:
      return {
        ...state,
        loadingById: {
          ...state.loadingById,
          [action.payload]: false,
        },
        error: action.payload,
      };

    default:
      // Handle reset store-specific state action
      if (action.type === resetStoreSpecificState.type) {
        console.log(`[order.slice] 🔄 Received resetStoreSpecificState, resetting to initialState`);
        return initialState;
      }
      return state;
  }
};

export default orderReducer;
